import { useState } from "react";
import { LuLoaderCircle } from "react-icons/lu";
import { FeedbackApi } from "../../api/feedbackApi";
import { useProjectStore } from "../../core_stores/useProjectStore";
import { useFeedbackModal } from "../../providers/FeedbackModalProvider";
import { useSnackbar } from "../../providers/SnackbarProvider";
import { FeedbackType } from "./FeedbackModal";

export default function FeedbackForm({ type }: { type: FeedbackType }) {
  const { showSnackbar } = useSnackbar();
  const { close: closeFeedbackModal } = useFeedbackModal();

  const [feedbackText, setFeedbackText] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const getTitle = () => {
    if (type === FeedbackType.BUG) return "Report a bug";
    if (type === FeedbackType.FEATURE) return "Request a feature";
  };

  const getSubtitle = () => {
    if (type === FeedbackType.BUG)
      return "Please describe the bug you're experiencing";
    if (type === FeedbackType.FEATURE)
      return "Please describe the feature you're requesting";
  };

  const getPlaceholder = () => {
    if (type === FeedbackType.BUG) return "Please describe the bug here...";
    if (type === FeedbackType.FEATURE)
      return "Please describe the feature here...";
  };

  const handleSubmit = async () => {
    const projectId = useProjectStore.getState().projectId;

    if (projectId === null) {
      showSnackbar(
        "Unexpected error occurred. Refresh the page and try again.",
        {
          type: "error",
        }
      );
      return;
    }

    setIsSubmitting(true);

    let feedbackType: "bug report" | "feature request" | undefined = undefined;
    if (type === FeedbackType.BUG) feedbackType = "bug report";
    if (type === FeedbackType.FEATURE) feedbackType = "feature request";

    try {
      await FeedbackApi.sendFeedback(projectId, feedbackType!, feedbackText!);
      setIsSubmitting(false);
      showSnackbar("Feedback has been sent", {
        type: "success",
      });
      closeFeedbackModal();
    } catch (error) {
      setIsSubmitting(false);
      showSnackbar("Unexpected error occurred. Try again later.", {
        type: "error",
      });
      return;
    }
  };

  const isSubmitButtonDisabled =
    feedbackText === null || feedbackText?.trim() === "";
  const showError = feedbackText?.trim() === "";

  return (
    <div className="flex flex-col gap-2 w-full h-full px-10 pt-4 pb-10">
      <span className="font-semibold">{getTitle()}</span>
      <span>{getSubtitle()}</span>
      <div className="flex flex-col gap-1 h-full max-h-[50%]">
        <textarea
          className="border border-[#373B404D] dark:border-[#373B40]
          py-3 px-4 rounded-lg w-full h-full resize-none
          bg-transparent mt-4"
          placeholder={getPlaceholder()}
          value={feedbackText ?? ""}
          onChange={(e) => {
            setFeedbackText(e.target.value);
          }}
        />
        {showError && (
          <span className="text-red-500 text-sm mt-1">
            Field cannot be empty
          </span>
        )}
      </div>
      <div className="flex justify-end pt-10">
        <button
          id="feedback-form-send"
          className="w-[100px] h-[30px] rounded-full transition-all duration-300 
          bg-[#16A34A] text-white hover:brightness-125
          flex items-center justify-center"
          onClick={handleSubmit}
          disabled={isSubmitButtonDisabled || isSubmitting}
        >
          {isSubmitting ? (
            <LuLoaderCircle className="animate-spin" size={20} />
          ) : (
            "Send"
          )}
        </button>
      </div>
    </div>
  );
}
